// saves-io.ts — back the DOM menu's MenuSaves with the SAME SaveStore +
// HostOpenSaveGameStream the canvas LoadGameForm uses (HTML/DOM UI shell plan,
// Phase 2). Pure (no DOM), node-testable, shared by main.ts AND the tests so
// the slot enumeration / resume-slot exclusion can't drift.

import { HostOpenSaveGameStream, knGameReinitializeSave, type SaveStore } from '../io/savegame.ts';
import type { Stream } from '../../../sim/src/sm/stream.ts';
import type { MenuSaves, SaveRow } from './menu-controller.ts';

/** a single displayed slot row of the Load/Save form's fixed list. */
export interface SaveSlotRow {
  slot: number; // nGame
  text: string; // "HH:MM name" (occupied) or "-- Empty --"
  occupied: boolean;
}

// the canvas Load/Save row time stamp — "HH:MM" (loadsave.ts Init: sprintf "%02d:%02d").
export function formatSaveWhen(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

// the FIXED slot list the canvas LoadGameForm.Init builds (loadsave.ts:117-157): occupied
// slots as "HH:MM name", the rest "-- Empty --", over max(minSlots, highestSlot+1) rows
// (≈ the canvas cEntries = rows that fit the list box). Shared by the shell load screen AND
// the in-game load/save overlay so the two can't drift.
export function saveSlotRows(rows: readonly SaveRow[], minSlots = 10): SaveSlotRow[] {
  const bySlot = new Map(rows.map((r) => [r.slot, r] as const));
  const maxSlot = rows.reduce((m, r) => Math.max(m, r.slot), -1);
  const n = Math.max(minSlots, maxSlot + 1);
  const out: SaveSlotRow[] = [];
  for (let i = 0; i < n; i++) {
    const sv = bySlot.get(i);
    out.push(
      sv !== undefined
        ? {
            slot: i,
            text: `${formatSaveWhen(sv.whenMs)}  ${sv.name || `Slot ${i}`}`,
            occupied: true,
          }
        : { slot: i, text: '-- Empty --', occupied: false },
    );
  }
  return out;
}

// the default-selected slot (loadsave.ts SelectLast): LOAD → the most-recently-saved slot
// (m_nGameLast, or 0 if none); SAVE → m_nGameLast + 1, wrapping to 0 at the list end (the
// next slot after the latest save).
export function defaultSaveSlot(
  rows: readonly SaveRow[],
  mode: 'load' | 'save',
  minSlots = 10,
): number {
  const recent = rows.reduce<SaveRow | null>(
    (a, b) => (a === null || b.whenMs > a.whenMs ? b : a),
    null,
  );
  const recentSlot = recent !== null ? recent.slot : -1;
  if (mode === 'load') return recentSlot < 0 ? 0 : recentSlot;
  const n = saveSlotRows(rows, minSlots).length;
  const next = recentSlot + 1;
  return next === n ? 0 : next;
}

export function makeMenuSaves(store: SaveStore): MenuSaves {
  return {
    // user saves only (the auto-resume slot knGameReinitializeSave is excluded,
    // as the canvas LoadGameForm count does), newest first
    list: (): SaveRow[] =>
      store
        .list()
        .filter((m) => m.nGame !== knGameReinitializeSave)
        .sort((a, b) => b.msDate - a.msDate)
        .map((m) => ({ slot: m.nGame, name: m.szName, whenMs: m.msDate })),
    // the SAME open the canvas LoadGameForm uses (loadsave.ts) — fDelete=false,
    // so loading a valid save preserves it (OpenSaveGameStream.Close only deletes
    // on read FAILURE, ported C++ temp-file behavior, identical for both menus)
    open: (slot: number): Stream | null => HostOpenSaveGameStream(store, slot),
  };
}
